// Helpers for displaying server-provided date/datetime values in the user's
// LOCAL timezone correctly.
//
// PostgreSQL DATE columns (e.g. bookings.check_in, bookings.check_out) come
// through the API as ISO strings with a UTC offset like "2026-08-02T00:00:00.000Z".
// `new Date(s).toLocaleDateString()` would shift those back a day in any
// timezone west of UTC (the date 2026-08-02 UTC midnight is 2026-08-01
// 8 PM EDT). Use parseLocalDate for any column that's logically a date,
// not a moment in time.
//
// PostgreSQL TIMESTAMPTZ columns (created_at, signed_at, etc.) come through
// with proper UTC info — `new Date(s).toLocaleString()` handles those
// correctly without help. Don't use these helpers for those.

export function parseLocalDate(val) {
  if (val instanceof Date) return val;
  if (val == null) return null;
  const ymd = String(val).slice(0, 10);
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function formatLocalDate(val, options) {
  const d = parseLocalDate(val);
  if (!d) return '';
  // Default to en-US so admins in non-EN browsers don't see Japanese
  // (or any other locale's) date formatting on tenant data. Callers
  // can still pass `locale` in options to override for genuine
  // internationalization later.
  const { locale = 'en-US', ...rest } = options || {};
  return d.toLocaleDateString(locale, rest);
}

// Format a JS Date as YYYY-MM-DD using the Date's LOCAL calendar fields.
// Inverse of parseLocalDate.
//
// Use this for Date objects that represent "the calendar cell the user
// clicked" — constructed via `new Date(year, month, day)` and intended
// as wall-clock carriers, not instants. Reading via local accessors
// avoids the `.toISOString().split('T')[0]` shift bug that bites users
// whose browser TZ differs from the tenant TZ (a UTC-browser user
// clicking May 28 would otherwise see May 27 stored, since local
// midnight in UTC reads as 8pm prior day in any zone west of UTC).
//
// Returns '' for non-Date / NaN-Date / nullish input so callers can
// short-circuit without null-checks.
export function formatLocalYmd(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Format a server-provided TIMESTAMP / TIMESTAMPTZ in the business's
// configured timezone, NOT the browser's. Operations want "pickup at 2pm"
// to mean 2pm at the business location, not wherever the admin happens
// to be (a traveling-admin scenario). Pass settings.timezone from the
// BusinessSettingsContext for `timeZone`.
//
// Falls back to browser TZ when timeZone is null/undefined.
//
// Use for: created_at, signed_at, payment_date, scheduled_for, dispute log
// timestamps. NOT for date columns (use formatLocalDate for those).
export function formatLocalDateTime(val, { timeZone, locale = 'en-US', ...options } = {}) {
  if (val == null) return '';
  const d = val instanceof Date ? val : new Date(val);
  if (Number.isNaN(d.getTime())) return '';
  // Default to en-US so admins in non-EN browsers don't get Japanese
  // (or other) locale formatting on tenant timestamps. Anchoring the
  // timezone via `timeZone` is necessary but not sufficient — without
  // a locale, toLocaleString falls back to `navigator.language`, so a
  // Tokyo-Chrome admin sees Japanese characters on signed-at and
  // reminder displays even though the underlying time math is correct.
  return d.toLocaleString(locale, { timeZone, ...options });
}

// Format a TIME column value (e.g. "14:00:00" or "14:00") as customer-
// facing copy: "2:00 PM". No timezone conversion — TIME columns are
// wall-clock at the business location and surfaced as-is.
//
// Use for: bookings.pickup_time, bookings.return_time, and any other
// HH:mm wall-clock value where you want "2:00 PM" rather than the raw
// 24-hour string.
export function formatLocalTime(timeString) {
  if (!timeString) return '';
  const parts = String(timeString).split(':');
  if (parts.length < 2) return '';
  const hour = parseInt(parts[0], 10);
  const minute = parseInt(parts[1], 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return '';
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${String(minute).padStart(2, '0')} ${period}`;
}
